import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Bell, Radio, CheckCircle2, ChevronDown } from 'lucide-react';
import './missionControl.css';
import SEOHead from '../components/UI/SEOHead';
import TypewriterText from '../components/UI/TypewriterText';
import { waLink } from '../config';

/* ──────────────────────────────────────────────────────────────────────────
   CASOS DE USO — rediseño "mission control". Caso destacado con mockup de
   bandeja de WhatsApp, y tres situaciones más con su propia mini-UI
   ilustrativa (recordatorio, reloj/estado, sucursales). Estructura y
   animaciones portadas del mockup del usuario; copy real vía i18n.
   ────────────────────────────────────────────────────────────────────────── */

// Colores elegidos por el contenido de cada tarjeta, no arbitrarios:
// case_1 (recordatorio/campana) = ámbar, el color universal de alerta;
// case_2 (Agente IA activo) = turquesa, el mismo acento de marca que ya usa
// el resto del sitio para "IA/Agente" (ver mockup de bandeja arriba);
// case_3 (sucursales Norte/Centro/Sur) = azul, asociación clásica con
// mapas/ubicación. El acento vive solo en el ícono y el chip de estado de
// cada tarjeta (fondo tenue + borde + texto del color) — el shell de la
// tarjeta es neutro e igual para las tres, ver .uc-mockup-card.
const OTHER_CASES = [
  { key: 'case_1', accent: '#fbbf24', accentDim: 'rgba(251,191,36,.12)', accentBorder: 'rgba(251,191,36,.3)' },
  { key: 'case_2', accent: '#00e5d1', accentDim: 'rgba(0,229,209,.12)', accentBorder: 'rgba(0,229,209,.3)' },
  { key: 'case_3', accent: '#3b82f6', accentDim: 'rgba(59,130,246,.12)', accentBorder: 'rgba(59,130,246,.3)' },
] as const;

const UseCasesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="mc-page"
    >
      <SEOHead
        title="Casos de Uso"
        description="Descubre cómo clínicas dentales usan RurayHUB para automatizar WhatsApp, agendar citas y aumentar sus ventas con inteligencia artificial."
        path="/casos-uso"
      />

      {/* ── HERO ── */}
      <div style={{ position: 'relative', minHeight: '92vh', padding: '5rem 1.5rem 3rem', maxWidth: 1120, margin: '0 auto', overflow: 'hidden', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div aria-hidden style={{ position: 'absolute', top: '-15%', left: '50%', transform: 'translateX(-50%)', width: 800, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, var(--accent-purple-dim) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }} />
        {/* bottom en positivo (no negativo) y radio del gradiente más corto
            (45% en vez de 70%) para que se apague del todo ANTES del borde
            inferior del hero — el hero recorta overflow, así que un glow que
            colgara por fuera se veía cortado en una línea recta justo en el
            borde de la sección (la costura que se notaba). */}
        <div aria-hidden style={{ position: 'absolute', bottom: '4%', right: '2%', width: 560, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, var(--accent-teal-dim) 0%, transparent 45%)', filter: 'blur(70px)', pointerEvents: 'none' }} />
        <div aria-hidden className="uc-hero-grid" />

        {/* Texto: en una sola columna centrada, como al inicio */}
        <motion.span
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ position: 'relative', zIndex: 2, display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-primary)', opacity: 0.6 }}
        >
          <TypewriterText text={t('usecases_page.eyebrow')} />
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: 'relative', zIndex: 2, fontFamily: 'var(--font-heading)', fontSize: 'clamp(2.4rem, 5.5vw, 4.3rem)', fontWeight: 300, lineHeight: 1.08, letterSpacing: '-0.035em', color: 'var(--text-primary)', margin: '1rem auto 1.3rem', maxWidth: '27ch', textWrap: 'balance' as React.CSSProperties['textWrap'] }}
        >
          {t('usecases_page.h1_pre', 'Problemas reales de ')}
          <span className="grad-text" style={{ fontWeight: 800 }}>{t('usecases_page.h1_grad', 'clínicas dentales')}</span>
          {t('usecases_page.h1_post', ', resueltos')}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ position: 'relative', zIndex: 2, fontSize: '1.1rem', lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: '50ch', margin: 0 }}
        >
          {t('usecases_page.lead')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '.9rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '2rem' }}
        >
          <a href={waLink(t('usecases_page.wa_message'))} target="_blank" rel="noopener noreferrer" className="trial-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
            {t('usecases_page.cta_primary')}
          </a>
          <a href="/servicios" className="uc-ghost-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
            {t('usecases_page.cta_secondary')}
          </a>
        </motion.div>

        {/* Cue de scroll — el hero ahora ocupa ~92vh y centra su contenido,
            así que el caso destacado queda oculto debajo del fold a
            propósito (aparece con su propia animación al hacer scroll, ver
            más abajo); esto le avisa al usuario que hay más contenido. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          style={{ position: 'absolute', left: '50%', bottom: 'clamp(1rem, 4vh, 2.5rem)', transform: 'translateX(-50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.5rem' }}
        >
          <span className="mc-mono" style={{ fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--text-muted)' }}>
            {t('usecases_page.scroll_cue', 'Casos reales abajo')}
          </span>
          <motion.span
            animate={{ y: [0, 7, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'flex', color: 'var(--text-muted)' }}
          >
            <ChevronDown size={18} strokeWidth={2.2} />
          </motion.span>
        </motion.div>

      </div>

      {/* ── CASO DESTACADO ── */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(2.5rem, 4.5vw, 4rem) 1.5rem 1rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          style={{ display: 'grid', gridTemplateColumns: '1.1fr .9fr', gap: 'clamp(2rem, 4vw, 4rem)', alignItems: 'center', borderRadius: 24, padding: 'clamp(2rem, 4vw, 3.2rem)', position: 'relative', overflow: 'hidden' }}
          className="mc-grid-2"
        >
          <div style={{ position: 'relative', zIndex: 2 }}>
            <motion.span
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="uc-featured-tag"
              style={{ display: 'inline-flex', alignItems: 'center', fontSize: '.7rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '.35rem .85rem', borderRadius: 999, border: '1px solid rgba(139,92,246,.28)', background: 'rgba(139,92,246,.1)', marginBottom: '1.2rem' }}
            >
              {t('usecases_page.featured_tag')}
            </motion.span>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.7rem, 2.8vw, 2.3rem)', fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.25, letterSpacing: '-0.02em', margin: '0 0 1.4rem' }}>
              {t('usecases_page.featured_title_pre')}<span className="grad-text" style={{ fontWeight: 800 }}>{t('usecases_page.featured_title_grad')}</span>
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ paddingLeft: '1rem', borderLeft: '2px solid var(--border-lighter)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-primary)', marginBottom: '.35rem' }}>{t('usecases_page.situation_label')}</div>
                <p style={{ fontSize: '.92rem', lineHeight: 1.55, margin: 0, color: 'var(--text-secondary)' }}>{t('usecases_page.featured_situation')}</p>
              </div>
              <div style={{ paddingLeft: '1rem', borderLeft: '2px solid var(--accent-purple)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-primary)', marginBottom: '.35rem' }}>{t('usecases_page.solution_label')}</div>
                <p style={{ fontSize: '.92rem', lineHeight: 1.55, margin: 0, color: 'var(--text-secondary)' }}>{t('usecases_page.featured_solution')}</p>
              </div>
              <div style={{ paddingLeft: '1rem', borderLeft: '2px solid var(--accent-teal)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-primary)', marginBottom: '.35rem' }}>{t('usecases_page.result_label')}</div>
                <p style={{ fontSize: '.92rem', lineHeight: 1.55, margin: 0, color: 'var(--accent-teal)', fontWeight: 600 }}>{t('usecases_page.featured_result')}</p>
              </div>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 48 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="uc-inbox-card"
            style={{ position: 'relative', zIndex: 2, borderRadius: 18, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '.7rem' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '.2rem' }}>
              <span>{t('usecases_page.inbox_label', 'Bandeja')}</span>
              <span>{t('usecases_page.inbox_unread', '3 sin responder')}</span>
            </div>
            <div className="uc-inbox-row" style={{ display: 'flex', alignItems: 'center', gap: '.6rem', borderRadius: 10, padding: '.6rem .8rem' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-muted)', flexShrink: 0 }} />
              <span className="uc-inbox-text" style={{ fontSize: '.78rem' }}>¿Tienen turno para hoy?</span>
            </div>
            <div className="uc-inbox-row" style={{ display: 'flex', alignItems: 'center', gap: '.6rem', borderRadius: 10, padding: '.6rem .8rem' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-muted)', flexShrink: 0 }} />
              <span className="uc-inbox-text" style={{ fontSize: '.78rem' }}>¿Cuánto cuesta la limpieza?</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', background: 'var(--accent-teal-dim)', border: '1px solid rgba(0,229,209,.3)', borderRadius: 10, padding: '.6rem .8rem' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent-teal)', flexShrink: 0 }} />
              <span className="uc-inbox-text" style={{ fontSize: '.78rem' }}>Agente IA: cita agendada a las 4pm ✓</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── TRES SITUACIONES MÁS ── */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(5rem, 8vw, 7rem) 1.5rem 1rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', maxWidth: '52ch', margin: '0 auto clamp(3rem, 6vw, 4.5rem)' }}
        >
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 300, letterSpacing: '-0.03em', color: 'var(--text-primary)', margin: '0 0 .8rem' }}>{t('usecases_page.section_title')}</h2>
          <p style={{ fontSize: '1.05rem', lineHeight: 1.6, margin: 0, color: 'var(--text-secondary)' }}>{t('usecases_page.section_desc')}</p>
        </motion.div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
          {OTHER_CASES.map((c, i) => {
            const flip = i === 1; // "fuera de horario" invierte el orden como en el mockup
            const visual = (
              <motion.div
                initial={{ opacity: 0, x: -48 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {c.key === 'case_1' && (
                  <div className="uc-mockup-card" style={{ width: '100%', maxWidth: 320, padding: '1.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.9rem', marginBottom: '1.2rem' }}>
                      <span className="uc-icon-badge" style={{ background: c.accent }}>
                        <Bell size={20} strokeWidth={2.4} color="#0b0f14" />
                      </span>
                      <div>
                        <div style={{ fontSize: '.92rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '.2rem' }}>{t('usecases_page.reminder_sent', 'Recordatorio enviado')}</div>
                        <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>WhatsApp · {t('usecases_page.local_time', 'Hora local')} 9:42 PM</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: '1.2rem' }}>{t('usecases_page.reminder_body', 'Mañana 10:00 am — Limpieza dental con Dra. Ibarra. Responde SÍ para confirmar.')}</div>
                    <div style={{ textAlign: 'center' }}>
                      <span className="uc-pill" style={{ background: 'var(--accent-teal)', color: '#04211d' }}>
                        <CheckCircle2 size={14} strokeWidth={2.8} />
                        {t('usecases_page.reminder_confirmed', 'Confirmada')}
                      </span>
                    </div>
                  </div>
                )}
                {c.key === 'case_2' && (
                  <div className="uc-mockup-card" style={{ width: '100%', maxWidth: 320, padding: '1.9rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: '.8rem' }}>{t('usecases_page.local_time', 'Hora local')}</div>
                    <div style={{ fontSize: '2.6rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '1.1rem' }}>9:42 <span style={{ fontSize: '1.2rem', color: 'var(--text-muted)' }}>PM</span></div>
                    <span className="uc-pill" style={{ background: c.accent, color: '#0b0f14', margin: '0 auto' }}>
                      <Radio size={14} strokeWidth={2.6} className="uc-pulse-icon" />
                      {t('usecases_page.agent_active', 'Agente IA activo')}
                    </span>
                  </div>
                )}
                {c.key === 'case_3' && (
                  <div className="uc-mockup-card" style={{ width: '100%', maxWidth: 320, padding: '1.6rem', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.7rem' }}>
                    {[['Norte', 42], ['Centro', 65], ['Sur', 28]].map(([label, val]) => (
                      <div key={label as string} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '.62rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '.6rem' }}>{label}</div>
                        <div style={{ background: c.accent, borderRadius: 12, padding: '.85rem 0' }}>
                          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0b0f14' }}>{val}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
            const copy = (
              <motion.div
                initial={{ opacity: 0, x: -48 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.4 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                <span style={{ display: 'inline-flex', fontSize: '.7rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '.35rem .85rem', borderRadius: 999, border: `1px solid ${c.accentBorder}`, color: c.accent, background: c.accentDim, marginBottom: '1rem' }}>
                  {t(`usecases_page.${c.key}_tag`)}
                </span>
                <h3 style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.8rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1rem' }}>{t(`usecases_page.${c.key}_title`)}</h3>
                <p style={{ fontSize: '.95rem', lineHeight: 1.6, margin: '0 0 .8rem', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{t('usecases_page.situation_label')}:</strong> {t(`usecases_page.${c.key}_situation`)}
                </p>
                <p style={{ fontSize: '.95rem', lineHeight: 1.6, margin: '0 0 .8rem', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{t('usecases_page.solution_label')}:</strong> {t(`usecases_page.${c.key}_solution`)}
                </p>
                <p style={{ fontSize: '.95rem', lineHeight: 1.6, margin: 0, color: 'var(--accent-teal)', fontWeight: 600 }}>
                  {t('usecases_page.result_label')}: {t(`usecases_page.${c.key}_result`)}
                </p>
              </motion.div>
            );
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.25 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(2rem, 4vw, 4rem)', alignItems: 'center' }}
                className="mc-grid-2"
              >
                {flip ? (
                  <>
                    <div style={{ order: 2 }}>{copy}</div>
                    <div style={{ order: 1 }}>{visual}</div>
                  </>
                ) : (
                  <>
                    {visual}
                    {copy}
                  </>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(4.5rem, 9vh, 7rem) 0 clamp(5rem, 10vh, 8rem)', position: 'relative' }}>
        <div className="container">
          <motion.div
            className="prod-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative', overflow: 'hidden', borderRadius: 32,
              padding: 'clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem)',
              textAlign: 'center',
              backdropFilter: 'blur(16px)',
            }}
          >
            <div aria-hidden style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--accent-teal), transparent)', opacity: 0.55, pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: '#00e5d1', opacity: 0.16, filter: 'blur(100px)', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', bottom: -120, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'var(--accent-purple)', opacity: 0.08, filter: 'blur(110px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto' }}>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1.6rem', lineHeight: 1.12, letterSpacing: '-0.03em' }}
              >
                {t('usecases_page.cta_title')}
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.18, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', gap: '.9rem', justifyContent: 'center', flexWrap: 'wrap' }}
              >
                <a href={waLink(t('usecases_page.wa_message'))} target="_blank" rel="noopener noreferrer" className="trial-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
                  {t('usecases_page.cta_primary')}
                </a>
                <a href="/servicios" className="uc-ghost-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
                  {t('usecases_page.cta_secondary')}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .trial-btn {
          background: #fff;
          color: #000;
          border: none;
          transition: background 0.2s ease;
        }
        .trial-btn:hover { background: #e8e8ed; }
        [data-theme='light'] .trial-btn { background: #000; color: #fff; }
        [data-theme='light'] .trial-btn:hover { background: #1d1d1f; }

        .uc-ghost-btn {
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.04); color: var(--text-primary);
          border: 1px solid var(--border-lighter);
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .uc-ghost-btn:hover { background: rgba(255,255,255,0.07); border-color: var(--accent-teal); }

        /* Textura del hero — el fondo quedaba muy vacío alrededor del texto
           ahora que el hero centra todo en ~92vh; una grilla puntillada muy
           sutil (mismo lenguaje "mission control" del sitio) le da textura
           sin competir con el título ni los glows. Se desvanece hacia los
           bordes con un mask radial para no cortar en seco. */
        .uc-hero-grid {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
          background-image: radial-gradient(rgba(255,255,255,.09) 1px, transparent 1px);
          background-size: 28px 28px;
          -webkit-mask-image: radial-gradient(ellipse 60% 55% at 50% 45%, #000 0%, transparent 75%);
          mask-image: radial-gradient(ellipse 60% 55% at 50% 45%, #000 0%, transparent 75%);
        }
        [data-theme='light'] .uc-hero-grid { background-image: radial-gradient(rgba(0,0,0,.07) 1px, transparent 1px); }
        [data-theme='light'] .uc-ghost-btn { background: rgba(0,0,0,0.03); }
        [data-theme='light'] .uc-ghost-btn:hover { background: rgba(0,0,0,0.06); }

        .prod-card { background: rgba(255,255,255,0.02); border: 1px solid var(--border-light); }
        [data-theme='light'] .prod-card { background: var(--bg-elevated); border: 1px solid var(--border-lighter); box-shadow: 0 20px 45px -30px rgba(0,0,0,0.2); }

        /* Tag "Recepción saturada" — el texto lila claro (#d8c8fb, pensado
           para fondo oscuro) quedaba casi ilegible sobre el chip traslúcido
           en tema claro (lila muy pálido sobre lila muy pálido). En claro
           usa el morado de marca, con más peso, para contraste real. */
        .uc-featured-tag { color: #d8c8fb; }
        [data-theme='light'] .uc-featured-tag { color: var(--accent-purple); font-weight: 800; }

        /* Mockup de bandeja (caso destacado) — estaba fijo en tonos para
           tema oscuro (fondo negro translúcido + texto gris claro
           #e5e7eb), así que en tema claro se veía como una caja gris con
           texto casi invisible. Ahora sigue el tema real. */
        .uc-inbox-card { background: rgba(0,0,0,0.28); border: 1px solid var(--border-light); }
        [data-theme='light'] .uc-inbox-card { background: rgba(0,0,0,0.035); border: 1px solid var(--border-lighter); }
        .uc-inbox-row { background: rgba(255,255,255,.04); border: 1px solid var(--border-light); }
        [data-theme='light'] .uc-inbox-row { background: rgba(0,0,0,.035); border: 1px solid var(--border-lighter); }
        .uc-inbox-text { color: #e5e7eb; }
        [data-theme='light'] .uc-inbox-text { color: var(--text-primary); }

        /* Mini-mockups de "tres situaciones más" — mismo shell neutro para
           las tres (igual criterio que .prod-card, el resto de tarjetas del
           sitio): fondo/borde parejos, sin llenarlas de color ni glow. Cada
           caso solo aporta su acento en el ícono y el chip de estado —
           mismo lenguaje que usan los tags del resto de la página (fondo
           tenue + borde + texto del color, ver "featured_tag" arriba). El
           relieve es neutro (sombra + leve elevación en hover), no teñido:
           no compite con el acento puntual del ícono/chip.  */
        .uc-mockup-card {
          background: rgba(255,255,255,.03);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          box-shadow: 0 24px 48px -28px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.05);
          transition: transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s ease;
        }
        .uc-mockup-card:hover { transform: translateY(-4px); box-shadow: 0 30px 58px -28px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.07); }
        [data-theme='light'] .uc-mockup-card { background: var(--bg-elevated); border: 1px solid var(--border-lighter); box-shadow: 0 20px 45px -30px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.6); }

        /* Ícono del recordatorio — antes un emoji (🔔); ahora ícono real
           (lucide) sobre chip traslúcido del acento del caso, mismo patrón
           que el resto de badges del sitio. */
        .uc-icon-badge {
          width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }

        /* Chips de estado (Confirmada / Agente IA activo) — fondo tenue +
           borde + texto del acento, igual criterio que los demás tags de
           la página (no color sólido, para no competir con el shell). */
        .uc-pill {
          display: inline-flex; align-items: center; gap: .4rem;
          font-size: .78rem; font-weight: 700; letter-spacing: .01em;
          padding: .4rem .9rem; border-radius: 999px;
        }
        @keyframes uc-pulse-icon { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
        .uc-pulse-icon { animation: uc-pulse-icon 1.7s ease-in-out infinite; }

        .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, rgba(8,8,8,0.6) 100%);
        }
        [data-theme='light'] .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }
      `}</style>
    </motion.div>
  );
};

export default UseCasesPage;
